<template>
  <div v-if="visible" class="md-drill">
    <div class="md-drill__backdrop" @click="$emit('close')"></div>
    <div class="md-drill__panel">
      <div class="md-drill__grip"></div>
      <div class="md-drill__head">
        <div class="md-drill__heading">
          <h3 class="md-drill__title">{{ title }}</h3>
          <div class="md-drill__totals">
            <span v-if="total != null">{{ ftdCount != null ? ftdCount : total }} {{ $t('mobileDash.drill.items') }}</span>
            <span v-if="sum != null" class="md-drill__sum">{{ convertToEURO(sum) }}</span>
          </div>
        </div>
        <button type="button" class="md-drill__close" @click="$emit('close')">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <div class="md-drill__body">
        <md-skeleton v-if="loading && !rows.length" :rows="6"/>
        <div v-else-if="error" class="md-drill__error">
          <div>{{ error }}</div>
          <b-button size="sm" variant="outline-primary" @click="fetch(1)">{{ $t('mobileDash.retry') }}</b-button>
        </div>
        <div v-else-if="!rows.length" class="md-drill__empty">{{ $t('mobileDash.noData') }}</div>
        <template v-else>
          <button
              v-for="tx in rows" :key="tx.id"
              type="button"
              class="md-drill__row"
              @click="openClient(tx)"
          >
            <span class="md-drill__main">
              <span class="md-drill__client">{{ clientName(tx) }}</span>
              <span class="md-drill__meta">
                #{{ tx.id }} · {{ tx.registrationTime | momentDateTime }}
              </span>
            </span>
            <span class="md-drill__right">
              <span class="md-drill__amount" :class="amountClass(tx)">{{ convertToEURO(tx.amount) }}</span>
              <span class="md-drill__chips">
                <span v-if="tx.isFtd" class="md-drill__chip md-drill__chip--ftd">FTD</span>
                <span v-else-if="tx.ftdType && tx.ftdType.startsWith('FTD_')" class="md-drill__chip md-drill__chip--ftd">
                  {{ tx.ftdType.replace('_', ' ') }}
                </span>
                <span v-if="tx.status !== 'APPROVED'" class="md-drill__chip">{{ tx.status }}</span>
              </span>
            </span>
          </button>
          <button v-if="rows.length < total" type="button" class="md-drill__more" :disabled="loading" @click="fetch(page + 1)">
            <b-spinner v-if="loading" small/>
            <template v-else>{{ $t('mobileDash.drill.loadMore') }}</template>
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import mixinBase from '@/components/mixin/mixinBase';
import MdSkeleton from '@/components/mobile/MdSkeleton';

export default {
  name: 'MdDrillSheet',
  components: { MdSkeleton },
  mixins: [mixinBase],
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, default: '' },
    filters: { type: Object, default: () => ({}) },
  },
  data: () => ({
    rows: [],
    clients: {},
    sum: null,
    ftdCount: null,
    total: 0,
    page: 1,
    loading: false,
    error: null,
  }),
  watch: {
    visible(open) {
      document.body.style.overflow = open ? 'hidden' : '';
      if (open) {
        this.rows = [];
        this.clients = {};
        this.sum = null;
        this.ftdCount = null;
        this.total = 0;
        this.error = null;
        this.fetch(1);
      }
    },
  },
  beforeDestroy() {
    document.body.style.overflow = '';
  },
  methods: {
    ...mapActions('monetary', ['getAllMonetary']),
    fetch(page) {
      this.loading = true;
      this.error = null;
      this.getAllMonetary({
        filter: this.filters,
        pagination: { page, size: 25 },
        sort: { field: 'registrationTime', direction: 'desc' },
      })
          .then(result => {
            const data = result.data || {};
            const transactions = data.monetaryTransactions || [];
            this.rows = page === 1 ? transactions : this.rows.concat(transactions);
            (data.clients || []).forEach(c => this.$set(this.clients, c.id, c));
            this.sum = data.sum != null ? data.sum : this.sum;
            this.ftdCount = data.ftdCount != null ? data.ftdCount : this.ftdCount;
            this.total = (result.pagination || {}).total || 0;
            this.page = page;
          })
          .catch(err => {
            this.error = this.getErrorDescription(err);
          })
          .finally(() => {
            this.loading = false;
          });
    },
    clientName(tx) {
      const clientId = tx.clientDetails && tx.clientDetails.clientId;
      const client = clientId != null ? this.clients[clientId] : null;
      if (client) return `${client.firstName || ''} ${client.lastName || ''}`.trim() || clientId;
      return clientId != null ? `#${clientId}` : '—';
    },
    amountClass(tx) {
      if (tx.type === 'WITHDRAWAL' || (tx.amount || 0) < 0) return 'is-out';
      return 'is-in';
    },
    openClient(tx) {
      const clientId = tx.clientDetails && tx.clientDetails.clientId;
      if (clientId != null) this.showClient(clientId);
    },
  },
};
</script>

<style scoped>
.md-drill__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(17, 20, 26, 0.5);
  z-index: 1090;
  animation: md-drill-fade 0.2s ease;
}
.md-drill__panel {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1091;
  background: var(--md-card-bg, #fff);
  border-radius: 16px 16px 0 0;
  height: 82vh;
  display: flex;
  flex-direction: column;
  animation: md-drill-up 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  padding-bottom: env(safe-area-inset-bottom, 0);
}
.md-drill__grip {
  width: 38px;
  height: 4px;
  border-radius: 999px;
  background: rgba(138, 145, 156, 0.35);
  margin: 8px auto 0;
}
.md-drill__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.6rem;
  padding: 0.6rem 1rem 0.55rem;
  border-bottom: 1px solid var(--md-card-border, rgba(34, 41, 47, 0.08));
}
.md-drill__title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
}
.md-drill__totals {
  display: flex;
  gap: 0.6rem;
  margin-top: 2px;
  font-size: 11.5px;
  color: var(--md-muted, #8a919c);
}
.md-drill__sum {
  font-weight: 700;
  color: #1f9d61;
}
.md-drill__close {
  border: none;
  background: rgba(138, 145, 156, 0.12);
  width: 30px;
  height: 30px;
  border-radius: 999px;
  font-size: 14px;
  color: var(--md-muted, #8a919c);
  flex: 0 0 auto;
}
.md-drill__body {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0.5rem 0.75rem 1rem;
}
.md-drill__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  border-bottom: 1px solid var(--md-card-border, rgba(34, 41, 47, 0.06));
  padding: 0.55rem 0.25rem;
}
.md-drill__row:active { background: rgba(138, 145, 156, 0.08); }
.md-drill__main {
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.md-drill__client {
  font-size: 13px;
  font-weight: 600;
  color: var(--md-strong, #24292f);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.md-drill__meta {
  font-size: 10.5px;
  color: var(--md-muted, #8a919c);
}
.md-drill__right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex: 0 0 auto;
}
.md-drill__amount {
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.md-drill__amount.is-in { color: #1f9d61; }
.md-drill__amount.is-out { color: #d64545; }
.md-drill__chips { display: flex; gap: 4px; margin-top: 1px; }
.md-drill__chip {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.03em;
  padding: 1px 6px;
  border-radius: 999px;
  background: rgba(138, 145, 156, 0.14);
  color: var(--md-muted, #8a919c);
}
.md-drill__chip--ftd {
  background: rgba(115, 103, 240, 0.12);
  color: #7367f0;
}
.md-drill__more {
  display: block;
  width: 100%;
  margin-top: 0.5rem;
  padding: 0.6rem;
  border: 1px dashed rgba(138, 145, 156, 0.4);
  border-radius: 9px;
  background: transparent;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--primary, #7367f0);
}
.md-drill__error,
.md-drill__empty {
  text-align: center;
  padding: 1.6rem 1rem;
  font-size: 12.5px;
  color: var(--md-muted, #8a919c);
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  align-items: center;
}
@keyframes md-drill-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes md-drill-up {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

.dark-layout .md-drill__panel {
  --md-card-bg: #283046;
  --md-card-border: #3b4253;
  --md-strong: #d0d2d6;
}
</style>
